import { useCallback, useEffect, useRef, useState } from "react"

export type NotificationPermissionState = "granted" | "denied" | "default"

interface ShowNotificationOptions {
	title: string
	body: string
	icon?: string
	tag?: string
	threadId?: string
	/** Where a click goes when it isn't a thread; wins over threadId. */
	path?: string
	onNavigate?: (path: string) => void
}

interface UseBrowserNotificationsResult {
	isSupported: boolean
	permission: NotificationPermissionState
	requestPermission: () => Promise<NotificationPermissionState>
	showNotification: (options: ShowNotificationOptions) => void
}

export function useBrowserNotifications(): UseBrowserNotificationsResult {
	const [permission, setPermission] = useState<NotificationPermissionState>(
		typeof window !== "undefined" && "Notification" in window
			? Notification.permission
			: "default",
	)

	const isSupported = typeof window !== "undefined" && "Notification" in window
	const swRegistrationRef = useRef<ServiceWorkerRegistration | null>(null)

	// Each caller holds its own copy, so a grant made elsewhere (settings, the
	// composer) has to reach this one through the browser's change event.
	useEffect(() => {
		if (!isSupported) return
		setPermission(Notification.permission)
		let status: PermissionStatus | null = null
		const sync = () => setPermission(Notification.permission)
		navigator.permissions
			?.query({ name: "notifications" })
			.then((result) => {
				status = result
				result.addEventListener("change", sync)
			})
			.catch(() => undefined)
		return () => status?.removeEventListener("change", sync)
	}, [isSupported])

	// Register service worker — required for Firefox on HTTPS which doesn't support
	// new Notification() from the page context (only from service workers)
	useEffect(() => {
		if (!("serviceWorker" in navigator)) return

		navigator.serviceWorker
			.register(`${import.meta.env.BASE_URL}sw.js`)
			.then((registration) => {
				swRegistrationRef.current = registration
			})
			.catch(() => {
				// SW registration failed, will fall back to new Notification()
			})
	}, [])

	const requestPermission =
		useCallback(async (): Promise<NotificationPermissionState> => {
			if (!isSupported) return "denied"

			const result = await Notification.requestPermission()
			setPermission(result)
			return result
		}, [isSupported])

	const showNotification = useCallback(
		(options: ShowNotificationOptions) => {
			// Read live: the state above can lag a grant made a moment ago.
			if (!isSupported || Notification.permission !== "granted") return

			const notificationOptions: NotificationOptions = {
				body: options.body,
				icon: options.icon || "/static/icons/favicon-32x32.png",
				tag: options.tag,
				data: { threadId: options.threadId, path: options.path },
			}

			// Prefer SW notification — required for Firefox on HTTPS, works everywhere
			const swReg = swRegistrationRef.current
			if (swReg) {
				void swReg.showNotification(options.title, notificationOptions)
				return
			}

			// Fallback: direct Notification API (Chrome/Safari without SW)
			try {
				const notification = new Notification(
					options.title,
					notificationOptions,
				)

				notification.onclick = () => {
					window.focus()
					const path =
						options.path ??
						(options.threadId ? `/threads/${options.threadId}` : "/")
					if (options.onNavigate) {
						options.onNavigate(path)
					} else {
						window.location.href = path
					}
					notification.close()
				}

				setTimeout(() => notification.close(), 10000)
			} catch {
				// Silently fail if notification creation is blocked
			}
		},
		[isSupported],
	)

	return {
		isSupported,
		permission,
		requestPermission,
		showNotification,
	}
}
